import React, { useCallback, useState } from 'react';
import { Box, Button, Typography } from '@mui/joy';
import BottomSheet from '../../common/bottom-sheet';
import underline from '../../../assets/disclaimer_heading_underline.svg';
import useI18n from '@mono-farmart-web/i18n/hooks/usei18n';
import { keys } from '@mono-farmart-web/i18n/Keys';
import { ChevronRightRounded } from '@mui/icons-material';

interface DisclaimerProps {
	header?: string;
	text?: string;
	textId?: string;
	testId: string;
}

const disclaimerText = `You’re currently experiencing the beta version of FarMart AI, our latest step toward revolutionizing agri-tech with artificial intelligence. This version is a work in progress, and while we strive to offer a seamless experience, some features may still be in development.

🚀 AI-Powered Insights – FarMart AI is designed to enhance decision-making, improve efficiency, and provide smarter solutions for agricultural businesses.

⚖️ Fair Use & Data Policies – As part of our beta testing phase, we may collect anonymized data to improve system accuracy, user experience, and overall performance. Your privacy and security are our top priorities.`;

const Disclaimer: React.FC<DisclaimerProps> = ({
	header,
	text,
	textId,
	testId,
}) => {
	const [openSheet, setOpenSheet] = useState(false);
	const { t } = useI18n();

	const toggleModal = useCallback(() => {
		setOpenSheet(!openSheet);
	}, [openSheet]);

	return (
		<Box
			sx={{
				height: 24,
				width: '100%',
				//zIndex: 11,
				backgroundColor: 'common.white',
			}}
		>
			<Typography
				data-testid={`${testId}-open-button`}
				onClick={toggleModal}
				sx={{
					cursor: 'pointer',
					color: 'danger.outlinedDisabledColor',
					textAlign: 'center',
					display: 'flex',
					fontSize: 10,
					py: 0.5,
					backgroundColor: 'common.white',
					height: '100%',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				{t(keys.farmart_ai_beta_version)}
				<ChevronRightRounded
					sx={{
						width: '15px',
						height: '15px',
						color: 'text.tertiary',
						marginLeft: '2px',
					}}
				/>
			</Typography>

			<BottomSheet
				testId={`${testId}-bottom-sheet`}
				onToggled={toggleModal}
				toggled={openSheet}
			>
				{/* Wrapper for centering the header and image */}
				<div
					style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
				>
					<Typography
						level='title-md'
						sx={{
							background: 'linear-gradient(to right, #009506 0%, #004F3C 100%)',
							WebkitBackgroundClip: 'text',
							WebkitTextFillColor: 'transparent',
							display: 'inline-block',
							textAlign: 'center',
						}}
						data-testid={`${testId}-header`}
					>
						{header ?? t(keys.beta_version)}
					</Typography>
					<img
						src={underline}
						alt='underline'
						style={{
							display: 'block',
							marginTop: '4px',
							width: '100%', // Ensures it takes full width of text
							maxWidth: '120px', // Prevents it from being too large on wide screens
							paddingBottom: 16,
						}}
					/>
				</div>

				<Typography
					sx={{
						whiteSpace: 'pre-line',
						textAlign: 'left',
						color: 'text.primary',
						marginBottom: 2,
						wordWrap: 'break-word',
						fontSize: 14,
						paddingBottom: 16,
					}}
					data-testid={textId ?? `${testId}-text`}
				>
					{text ?? disclaimerText}
				</Typography>

				<Button
					onClick={toggleModal}
					data-testid={`${testId}-close-button`}
					sx={{ width: '100%' }}
				>
					{t(keys.okay_ai)}
				</Button>
			</BottomSheet>
		</Box>
	);
};

export default React.memo(Disclaimer);
